/**
 *
 * @module dustUrnReference
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/dust.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/dust.md#api-events
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/dust.md#service-events
 *
 */

/**
 *
 * @access private
 * @typedef SDK.Services.Internal.Dust.DustUrnReference
 * @type {Object}
 * @since 3.4.0
 * @desc Internal DUST API event hash reference.
 * Mapping of all SDK methods and events in a single location.
 *
 * @property {Object} account
 * @property {Object} configuration
 * @property {Object} content
 * @property {Object} identity
 * @property {Object} media
 * @property {Object} purchase
 * @property {Object} sdkSession
 * @property {Object} subscription
 * @property {Object} services
 *
 * @example SDK.Services.Internal.Dust.DustUrnReference.account.accountApi.getAccount
 * @example SDK.Services.Internal.Dust.DustUrnReference.services.account.accountClient.getCurrentAccount
 *
 */

/**
 *
 * @type {SDK.Services.Internal.Dust.DustUrnReference}
 *
 */
export default {
    account: {
        accountApi: {
            getAccount: 'urn:bamtech:dust:bamsdk:api:account:getAccount',
            updateAccount: 'urn:bamtech:dust:bamsdk:api:account:updateAccount',
            linkSubscriptionsFromDevice: 'urn:bamtech:dust:bamsdk:api:account:linkSubscriptionsFromDevice',
            authorize: 'urn:bamtech:dust:bamsdk:api:account:authorize',
            createAccount: 'urn:bamtech:dust:bamsdk:api:account:createAccount'
        }
    },
    commerce: {
        commerceApi: {
            priceOrder: 'urn:bamtech:dust:bamsdk:api:commerce:priceOrder',
            getLocationCandidates: 'urn:bamtech:dust:bamsdk:api:commerce:getLocationCandidate',
            submitOrder: 'urn:bamtech:dust:bamsdk:api:commerce:submitOrder',
            submitOrderWithPaymentMethod: 'urn:bamtech:dust:bamsdk:api:commerce:submitOrderWithPaymentMethod',
            checkOrderStatus: 'urn:bamtech:dust:bamsdk:api:commerce:checkOrderStatus',
            createPaymentMethod: 'urn:bamtech:dust:bamsdk:api:commerce:createPaymentMethod',
            getDefaultPaymentMethod: 'urn:bamtech:dust:bamsdk:api:commerce:getDefaultPaymentMethod',
            getPaymentCard: 'urn:bamtech:dust:bamsdk:api:commerce:getPaymentCard',
            getGuid: 'urn:bamtech:dust:bamsdk:api:commerce:getGuid'
        }
    },
    configuration: {
        configurationManager: {
            getConfiguration: 'urn:bamtech:dust:bamsdk:service:configuration:loadRemote'
        }
    },
    content: {
        contentApi: {
            query: 'urn:bamtech:dust:bamsdk:api:content:query',
            getSearchSuggestions: 'urn:bamtech:dust:bamsdk:api:content:getSearchSuggestions'
        }
    },
    customerService: {
        customerServiceApi: {
            createSupportCode: 'urn:bamtech:dust:bamsdk:api:customerService:createSupportCode'
        }
    },
    identity: {
        bam: {
            bamIdentityApi: {
                createIdentity: 'urn:bamtech:dust:bamsdk:api:identity:bam:createIdentity',
                getIdentity: 'urn:bamtech:dust:bamsdk:api:identity:bam:getIdentity',
                authenticate: 'urn:bamtech:dust:bamsdk:api:identity:bam:authenticate',
                updateIdentity: 'urn:bamtech:dust:bamsdk:api:identity:bam:updateIdentity',
                updatePassword: 'urn:bamtech:dust:bamsdk:api:identity:bam:updatePassword',
                updateEmail: 'urn:bamtech:dust:bamsdk:api:identity:bam:updateEmail',
                resetPassword: 'urn:bamtech:dust:bamsdk:api:identity:bam:resetPassword',
                requestPasswordReset: 'urn:bamtech:dust:bamsdk:api:identity:bam:requestPasswordReset',
                requiresAuthentication: 'urn:bamtech:dust:bamsdk:api:identity:bam:requiresAuthentication',
                getAuthenticationFlow: 'urn:bamtech:dust:bamsdk:api:identity:bam:getAuthenticationFlow',
                requestOneTimePasscode: 'urn:bamtech:dust:bamsdk:api:identity:bam:requestOneTimePasscode',
                authenticateWithPasscode: 'urn:bamtech:dust:bamsdk:api:identity:bam:authenticateWithPasscode'
            }
        }
    },
    media: {
        mediaApi: {
            createPlaybackSession: 'urn:bamtech:dust:bamsdk:api:media:createPlaybackSession',
            fetch: 'urn:bamtech:dust:bamsdk:api:media:fetch',
            getSpriteSheetThumbnailSets: 'urn:bamtech:dust:bamsdk:api:media:getSpriteSheetThumbnailSets',
            getSpriteSheetThumbnailSet: 'urn:bamtech:dust:bamsdk:api:media:getSpriteSheetThumbnailSet',
            getSpriteSheetThumbnail: 'urn:bamtech:dust:bamsdk:api:media:getSpriteSheetThumbnail',
            getBifThumbnailSets: 'urn:bamtech:dust:bamsdk:api:media:getBifThumbnailSets',
            getBifThumbnailSet: 'urn:bamtech:dust:bamsdk:api:media:getBifThumbnailSet',
            getBifThumbnail: 'urn:bamtech:dust:bamsdk:api:media:getBifThumbnail'
        },
        playbackSession: {
            prepare: 'urn:bamtech:dust:bamsdk:api:media:playbackSession:prepare',
            release: 'urn:bamtech:dust:bamsdk:api:media:playbackSession:release'
        },
        encryptionKeyProvider: {
            getEncryptionKey: 'urn:bamtech:dust:bamsdk:api:media:encryptionKeyProvider:getEncryptionKey'
        }
    },
    messaging: {
        messagingApi: {
            registerChannel: 'urn:bamtech:dust:bamsdk:api:messaging:registerChannel'
        }
    },
    paywall: {
        paywallApi: {
            getPaywall: 'urn:bamtech:dust:bamsdk:api:paywall:getPaywall'
        }
    },
    purchase: {
        purchaseApi: {
            redeem: 'urn:bamtech:dust:bamsdk:api:purchase:redeem',
            restore: 'urn:bamtech:dust:bamsdk:api:purchase:restore'
        }
    },
    sdkSession: {
        createSdkSession: 'urn:bamtech:dust:bamsdk:api:session:create',
        initialize: 'urn:bamtech:dust:bamsdk:api:session:initialize',
        reset: 'urn:bamtech:dust:bamsdk:api:session:reset',
        reauthorize: 'urn:bamtech:dust:bamsdk:api:session:reauthorize',
        getAccessState: 'urn:bamtech:dust:bamsdk:api:session:getAccessState',
        restoreAccessState: 'urn:bamtech:dust:bamsdk:api:session:restoreAccessState',
        getSessionInfo: 'urn:bamtech:dust:bamsdk:api:session:getSessionInfo',
        createCustomerSupportCode: 'urn:bamtech:dust:bamsdk:api:session:createCustomerSupportCode',
        authorize: 'urn:bamtech:dust:bamsdk:api:session:authorize'
    },
    subscription: {
        subscriptionApi: {
            getSubscriptions: 'urn:bamtech:dust:bamsdk:api:subscription:getSubscriptions'
        }
    },
    userActivity: {
        personalization: {
            contentRecommendationAward: 'urn:bamtech:dust:bamsdk:app:content-recommendation-reward:v1'
        },
        glimpse: {
            appLaunched: 'urn:dss:glimpse:event:app-lifecycle:app-launched',
            contentSelected: 'urn:dss:glimpse:event:track-action:content-selected',
            searchViewLoaded: 'urn:dss:glimpse:event:track-action:search-view-loaded',
            playbackSelected: 'urn:dss:glimpse:event:track-action:playback-selected',
            playbackExited: 'urn:dss:glimpse:event:track-action:playback-exited',
            purchaseCompleted: 'urn:dss:glimpse:event:commerce:purchase-completed',
            viewLoaded: 'urn:dss:glimpse:event:track-state:view-loaded'
        }
    },
    userProfile: {
        userProfileApi: {
            createUserProfile: 'urn:bamtech:dust:bamsdk:api:account:createUserProfile',
            deleteUserProfile: 'urn:bamtech:dust:bamsdk:api:account:deleteUserProfile',
            getActiveUserProfile: 'urn:bamtech:dust:bamsdk:api:account:getActiveUserProfile',
            getUserProfile: 'urn:bamtech:dust:bamsdk:api:account:getUserProfile',
            getUserProfiles: 'urn:bamtech:dust:bamsdk:api:account:getUserProfiles',
            setActiveUserProfile: 'urn:bamtech:dust:bamsdk:api:account:setActiveUserProfile',
            updateUserProfile: 'urn:bamtech:dust:bamsdk:api:account:updateUserProfile'
        }
    },
    services: {
        account: {
            accountClient: {
                createAccount: 'urn:bamtech:dust:bamsdk:service:account:createAccount',
                createAccountGrant: 'urn:bamtech:dust:bamsdk:service:account:createAccountGrant',
                getCurrentAccount: 'urn:bamtech:dust:bamsdk:service:account:getCurrentAccount',
                updateAttributes: 'urn:bamtech:dust:bamsdk:service:account:updateAttributes',
                createUserProfile: 'urn:bamtech:dust:bamsdk:service:account:createUserProfile',
                deleteUserProfile: 'urn:bamtech:dust:bamsdk:service:account:deleteUserProfile',
                getUserProfile: 'urn:bamtech:dust:bamsdk:service:account:getUserProfile',
                getUserProfiles: 'urn:bamtech:dust:bamsdk:service:account:getUserProfiles',
                setActiveUserProfile: 'urn:bamtech:dust:bamsdk:service:account:setActiveUserProfile',
                getActiveUserProfile: 'urn:bamtech:dust:bamsdk:service:account:getActiveUserProfile',
                updateUserProfile: 'urn:bamtech:dust:bamsdk:service:account:updateUserProfile'
            }
        },
        commerce: {
            commerceClient: {
                generateGuid: 'urn:bamtech:dust:bamsdk:service:commerce:generateGuid',
                priceOrder: 'urn:bamtech:dust:bamsdk:service:commerce:priceOrder',
                lookupByZipCode: 'urn:bamtech:dust:bamsdk:service:commerce:lookupByZipCode',
                submitOrder: 'urn:bamtech:dust:bamsdk:service:commerce:submitOrder',
                submitOrderWithPaymentMethod: 'urn:bamtech:dust:bamsdk:service:commerce:submitOrderWithPaymentMethod',
                getOrderStatus: 'urn:bamtech:dust:bamsdk:service:commerce:getOrderStatus',
                createPaymentMethod: 'urn:bamtech:dust:bamsdk:service:commerce:createPaymentMethod',
                getDefaultPaymentMethod: 'urn:bamtech:dust:bamsdk:service:commerce:getDefaultPaymentMethod',
                getPaymentCard: 'urn:bamtech:dust:bamsdk:service:commerce:getPaymentCard'
            }
        },
        configuration: {
            configurationClient: {
                getConfiguration: 'urn:bamtech:dust:bamsdk:service:configuration:getConfiguration'
            }
        },
        content: {
            contentClient: {
                query: 'urn:bamtech:dust:bamsdk:service:content:query',
                getSearchSuggestions: 'urn:bamtech:dust:bamsdk:service:content:getSearchSuggestions'
            }
        },
        customerService: {
            customerServiceClient: {
                createSupportCode: 'urn:bamtech:dust:bamsdk:service:customerService:createSupportCode'
            }
        },
        device: {
            deviceClient: {
                createDeviceGrant: 'urn:bamtech:dust:bamsdk:service:device:createDeviceGrant'
            }
        },
        drm: {
            drmClient: {
                getSilkKey: 'urn:bamtech:dust:bamsdk:service:drm:silkKey',
                getPlayReadyLicense: 'urn:bamtech:dust:bamsdk:service:drm:playReadyLicense',
                getFairPlayCertificate: 'urn:bamtech:dust:bamsdk:service:drm:fairPlayCertificate',
                getFairPlayLicense: 'urn:bamtech:dust:bamsdk:service:drm:fairPlayLicense',
                getWidevineCertificate: 'urn:bamtech:dust:bamsdk:service:drm:widevineCertificate',
                getWidevineLicense: 'urn:bamtech:dust:bamsdk:service:drm:widevineLicense'
            }
        },
        identity: {
            bam: {
                bamIdentityClient: {
                    getIdentity: 'urn:bamtech:dust:bamsdk:service:identity:bam:getIdentity',
                    updateAttributes: 'urn:bamtech:dust:bamsdk:service:identity:bam:updateAttributes',
                    authenticate: 'urn:bamtech:dust:bamsdk:service:identity:bam:identityLogin',
                    createIdentity: 'urn:bamtech:dust:bamsdk:service:identity:bam:createIdentity',
                    changeEmail: 'urn:bamtech:dust:bamsdk:service:identity:bam:changeEmail',
                    changePassword: 'urn:bamtech:dust:bamsdk:service:identity:bam:changePassword',
                    requestPasswordReset: 'urn:bamtech:dust:bamsdk:service:identity:bam:requestPasswordReset',
                    resetPassword: 'urn:bamtech:dust:bamsdk:service:identity:bam:resetPassword',
                    check: 'urn:bamtech:dust:bamsdk:service:identity:bam:check',
                    requestOneTimePasscode: 'urn:bamtech:dust:bamsdk:service:identity:bam:otpRequest',
                    redeemOneTimePasscode: 'urn:bamtech:dust:bamsdk:service:identity:bam:otpRedeem'
                }
            }
        },
        internal: {
            telemetry: {
                telemetryClient: {
                    postEvents: 'urn:bamtech:dust:bamsdk:service:telemetry:postEvent'
                }
            }
        },
        media: {
            adEngine: {
                adEngineClient: {
                    updateCookies: 'urn:bamtech:dust:bamsdk:service:adEngine:setToken',
                    getCookies: 'urn:bamtech:dust:bamsdk:service:adEngine:setTokenRedirect'
                }
            },
            mediaClient: {
                getMediaPayload: 'urn:bamtech:dust:bamsdk:service:media:mediaPayload',
                createAuthCookie: 'urn:bamtech:dust:bamsdk:service:media:createAuthCookie',
                getSpriteSheetThumbnails: 'urn:bamtech:dust:bamsdk:service:media:spriteSheetThumbnails',
                getSpriteSheetThumbnail: 'urn:bamtech:dust:bamsdk:service:media:spriteSheetThumbnail',
                getBifThumbnails: 'urn:bamtech:dust:bamsdk:service:media:service:media:bifThumbnails',
                downloadBifThumbnail: 'urn:bamtech:dust:bamsdk:service:media:bifThumbnail'
            }
        },
        paywall: {
            paywallClient: {
                getPaywall: 'urn:bamtech:dust:bamsdk:service:paywall:paywall'
            }
        },
        purchase: {
            purchaseClient: {
                redeemPurchases: 'urn:bamtech:dust:bamsdk:service:purchase:redeemPurchases',
                restorePurchases: 'urn:bamtech:dust:bamsdk:service:purchase:restorePurchases',
                getTokens: 'urn:bamtech:dust:bamsdk:service:purchase:getTokens'
            }
        },
        session: {
            sessionClient: {
                getInfo: 'urn:bamtech:dust:bamsdk:service:session:getInfo'
            }
        },
        subscription: {
            subscriptionClient: {
                linkSubscriptionsFromDevice: 'urn:bamtech:dust:bamsdk:service:subscription:linkSubscriptionsFromDevice',
                getSubscriptions: 'urn:bamtech:dust:bamsdk:service:subscription:getSubscriptions'
            }
        },
        token: {
            tokenClient: {
                exchange: 'urn:bamtech:dust:bamsdk:service:token:exchange'
            }
        }
    },
    streamSample: 'urn:mlbam:api:stream-sample',
    bamSdk: 'urn:bamtech:dust:bamsdk'
};



// WEBPACK FOOTER //
// ./src/services/internal/dust/dustUrnReference.js